import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { CheckCircle, Circle, X, Rocket, ArrowRight } from "@phosphor-icons/react";
import api, { formatApiError, errContext } from "../lib/api";

const CONFETTI_COLORS = ["#FF3E00", "#09090b", "#10b981", "#fbbf24", "#ffffff"];

function fireCelebration() {
  const duration = 2500;
  const end = Date.now() + duration;
  // Left + right symmetrical bursts, staggered, using brand colors
  (function frame() {
    confetti({
      particleCount: 4,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.6 },
      colors: CONFETTI_COLORS,
      scalar: 1.1,
    });
    confetti({
      particleCount: 4,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.6 },
      colors: CONFETTI_COLORS,
      scalar: 1.1,
    });
    if (Date.now() < end) requestAnimationFrame(frame);
  }());
  // Big center burst at the end for the crescendo
  setTimeout(() => {
    confetti({
      particleCount: 120,
      spread: 100,
      origin: { y: 0.5 },
      colors: CONFETTI_COLORS,
      scalar: 1.3,
      ticks: 300,
    });
  }, duration - 400);
}

/**
 * OnboardingChecklist — 5-step activation panel shown on Dashboard.
 * Auto-detects step completion from real backend state (no manual toggling).
 * Persists dismissal to `tenants.onboarding_dismissed_at` on the backend.
 */
function ChecklistItem({ step, index }) {
  return (
    <li
      data-testid={`onboarding-step-${step.key}`}
      className={`flex items-center gap-4 px-5 py-3 ${step.done ? "bg-emerald-50/40" : ""}`}
    >
      <div className="flex-shrink-0">
        {step.done ? (
          <CheckCircle size={22} weight="fill" className="text-emerald-600" />
        ) : (
          <Circle size={22} weight="regular" className="text-zinc-300" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className={`flex items-center gap-2 ${step.done ? "text-zinc-400 line-through" : "text-zinc-950"}`}>
          <span className="font-mono text-[10px] tracking-[0.15em] text-zinc-400 tnum">0{index + 1}</span>
          <span className="font-semibold text-sm">{step.label}</span>
        </div>
        <div className="text-[11px] text-zinc-500 mt-0.5">{step.description}</div>
      </div>
      {!step.done && (
        <Link
          to={step.cta_href}
          data-testid={`onboarding-cta-${step.key}`}
          className="flex-shrink-0 font-mono text-[10px] tracking-[0.12em] uppercase border border-zinc-950 px-3 py-1.5 hover:bg-zinc-950 hover:text-white transition-colors inline-flex items-center gap-1.5"
        >
          {step.cta_label} <ArrowRight size={11} weight="bold" />
        </Link>
      )}
    </li>
  );
}

export default function OnboardingChecklist() {
  const [state, setState] = useState(null);
  const [dismissing, setDismissing] = useState(false);
  const celebrateInFlightRef = useRef(false);   // dedupes StrictMode double-effect
  const alreadyFiredRef = useRef(false);        // prevents double confetti in dev

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/onboarding/status");
      setState(data);
    } catch {
      setState(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Celebrate the FIRST time this tenant hits 100%. Refs guard against
  // React StrictMode's double-mount which would otherwise fire two POSTs
  // — the second returns first_time:false and swallows the celebration.
  useEffect(() => {
    if (!state) return;
    if (state.pct_complete !== 100) return;
    if (state.celebrated) return;
    if (celebrateInFlightRef.current) return;   // second StrictMode call short-circuits
    celebrateInFlightRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/onboarding/celebrate");
        if (data.first_time && !alreadyFiredRef.current) {
          alreadyFiredRef.current = true;
          fireCelebration();
          toast.success("You're all set up! Every activation step is done.", { duration: 6000 });
        }
        // Local state update is safe — we always want to reflect the celebrated flag
        setState((s) => (s ? { ...s, celebrated: true } : s));
      } catch { /* silent — celebration is best-effort */ }
    })();
  }, [state]);

  const dismiss = async () => {
    if (!window.confirm("Hide the onboarding checklist for good?")) return;
    setDismissing(true);
    try {
      await api.post("/onboarding/dismiss");
      toast.success("Checklist dismissed");
      setState((s) => ({ ...s, dismissed: true }));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, errContext(err)));
    } finally { setDismissing(false); }
  };

  if (!state || state.dismissed) return null;
  // Hide when 100% complete — no reason to nag once everything's done
  if (state.pct_complete === 100) return null;

  return (
    <section
      className="mb-8 border-2 border-zinc-950 bg-gradient-to-br from-white via-white to-zinc-50"
      data-testid="onboarding-checklist"
    >
      <div className="border-b border-zinc-950 bg-zinc-950 text-white px-5 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Rocket size={14} weight="fill" className="text-[#FF3E00]" />
          <span className="font-mono text-[11px] tracking-[0.2em]">/ GET STARTED WITH SMARTERP</span>
          {state.is_enterprise_deal && (
            <span className="font-mono text-[9px] tracking-[0.15em] bg-[#FF3E00] px-1.5 py-0.5">ENTERPRISE</span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] tracking-[0.12em] text-zinc-400" data-testid="onboarding-progress-text">
              {state.done_count} / {state.total_count}
            </span>
            <div className="w-32 h-1.5 bg-zinc-800 relative">
              <div
                className="absolute inset-y-0 left-0 bg-[#FF3E00] transition-all"
                style={{ width: `${state.pct_complete}%` }}
                data-testid="onboarding-progress-bar"
              />
            </div>
            <span className="font-mono text-[10px] tracking-[0.12em] text-white tnum">{state.pct_complete}%</span>
          </div>
          <button
            onClick={dismiss}
            disabled={dismissing}
            data-testid="onboarding-dismiss-btn"
            title="Dismiss for good"
            className="text-zinc-500 hover:text-white disabled:opacity-50">
            <X size={14} />
          </button>
        </div>
      </div>

      <ul className="divide-y divide-zinc-100">
        {state.steps.map((step, i) => (
          <ChecklistItem key={step.key} step={step} index={i} />
        ))}
      </ul>
    </section>
  );
}
